import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '~/store/types';
import { authenticationSelector } from '../../../authentication/ducks/selectors';
import {
	selectLocale,
	selectIsoCodeForLocale,
	selectFranchiseCode,
	selectSportsbookWebSocketConnectionErrorNotificationEnabled,
	selectSportsbookUnifiedFeedEnabled
} from '~/components/brand/ducks/selectors';
import { selectIsAuthenticated } from '../sportsbook-root-selectors';

import {
	disposeAndDisconnectFromSBApi,
	setupAndConnectToSBApi,
	reconnectToSBApiWebsocketClientAndKeepSubscriptions,
	selectIsSBApiReady
} from '../api/sportsbook-api-duck';
import { useCallback, useEffect } from 'react';
import { activateBonusTokens } from '../sportsbook-configuration-duck';
import { hideNotification } from '../../../notifications/ducks/notifications';
import SportsbookTextKeys from '../sportsbook-text-keys';
import {
	trackSportsbookSBApiWebSocketConnectionLostInformAndReloadNotification,
	trackSportsbookSBApiWebSocketConnectionLostInformNotification
} from '@comeon/analytics';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
import usePrevious from '~/hooks/use-previous';
import { setCookie, setPermanentCookie } from '~/store/cookies';

const UNIFIED_SBAPI_COOKIE_NAME = 'enableUnifiedSbapi';

/**
 * Provide the necessary handlers for the websocket connection to the Sportsbook API.
 */
const useHandleSBApiWebsocketConnection = () => {
	const dispatch: AppDispatch = useDispatch();
	const hasConnectionErrorNotification = useSelector(selectSportsbookWebSocketConnectionErrorNotificationEnabled);

	const handleConnected = useCallback(() => {
		// Todo: clear only specific notification
		dispatch(hideNotification());
	}, [dispatch]);

	const handleConnectionError = useCallback(
		(reconnectAttempt: number) => {
			if (reconnectAttempt === 0) {
				dispatch(
					showQueuedNotification({
						messageKey: SportsbookTextKeys.CONNECTION_LOST_INFORM,
						color: 'neutral',
						autoHide: false
					})
				);

				trackSportsbookSBApiWebSocketConnectionLostInformNotification();
			} else if (reconnectAttempt === 3) {
				dispatch(
					showQueuedNotification({
						messageKey: SportsbookTextKeys.CONNECTION_LOST_INFORM_AND_RELOAD,
						buttonTextKey: SportsbookTextKeys.CONNECTION_LOST_INFORM_AND_RELOAD_BUTTON_LABEL,
						color: 'neutral',
						buttonCB: () => {
							window.location.reload();
						},
						autoHide: false
					})
				);
				trackSportsbookSBApiWebSocketConnectionLostInformAndReloadNotification();
			}
		},
		[dispatch]
	);

	return {
		handleSBApiWebsocketConnected: hasConnectionErrorNotification ? handleConnected : undefined,
		handleSBApiWebsocketConnectionError: hasConnectionErrorNotification ? handleConnectionError : undefined
	};
};

/**
 * Try to re-establish the websocket connection to sbapi if the auth state changes.
 * Reason: Logged-in players might get forwarded to a "better" machine.
 * Not foolproof, might not get new machine if sbapi set-up is in loading state or
 * if client is already re-connecting.
 */
const useReconnectToSbapiOnAuthChange = () => {
	const isAuthenticatedMv = useSelector(authenticationSelector);
	const isSBApiReady = useSelector(selectIsSBApiReady);
	const previousIsAuthenticatedMv = usePrevious(isAuthenticatedMv);
	useEffect(() => {
		if (isSBApiReady && previousIsAuthenticatedMv !== isAuthenticatedMv) {
			void reconnectToSBApiWebsocketClientAndKeepSubscriptions();
		}
	}, [isSBApiReady, previousIsAuthenticatedMv, isAuthenticatedMv]);
};

/**
 * Initialize the Sportsbook API client and setup a websocket connection to the Sportsbook API.
 */
const useInitializeSportsbookApi = () => {
	const dispatch: AppDispatch = useDispatch();
	const locale = useSelector(selectLocale);
	const franchiseCode = useSelector(selectFranchiseCode);
	const localeCode = useSelector(selectIsoCodeForLocale);
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const isUnifiedFeedEnabled = useSelector(selectSportsbookUnifiedFeedEnabled);

	const { handleSBApiWebsocketConnected, handleSBApiWebsocketConnectionError } = useHandleSBApiWebsocketConnection();

	useEffect(() => {
		if (isAuthenticated) {
			dispatch(activateBonusTokens());
		}
	}, [dispatch, isAuthenticated]);

	useEffect(() => {
		dispatch(
			setupAndConnectToSBApi({
				franchiseCode,
				locale,
				localeCode,
				onSBApiWebsocketConnectionError: handleSBApiWebsocketConnectionError,
				onSBApiWebsocketConnected: handleSBApiWebsocketConnected
			})
		);

		return () => {
			dispatch(disposeAndDisconnectFromSBApi());
		};
	}, [
		locale,
		franchiseCode,
		localeCode,
		dispatch,
		handleSBApiWebsocketConnected,
		handleSBApiWebsocketConnectionError
	]);

	useReconnectToSbapiOnAuthChange();

	// Use a cookie to let nginx/other proxy forward to unified sbapi
	useEffect(() => {
		if (isUnifiedFeedEnabled) {
			setCookie(UNIFIED_SBAPI_COOKIE_NAME, 'true');
		} else {
			setPermanentCookie(UNIFIED_SBAPI_COOKIE_NAME, 'false', new Date());
		}
	}, [isUnifiedFeedEnabled]);
};

export default useInitializeSportsbookApi;
